import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDropzone } from 'react-dropzone';
import axios from 'axios';
import { toast } from 'sonner';
import { UploadSimple, FilePdf, FileDoc, ArrowLeft, TextT, Sparkle, X } from '@phosphor-icons/react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CreateJob = () => {
  const navigate = useNavigate();
  const [mode, setMode] = useState('text');
  const [title, setTitle] = useState('');
  const [content, setContent] = useState('');
  const [selectedFile, setSelectedFile] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const onDrop = useCallback((acceptedFiles) => {
    if (acceptedFiles.length > 0) {
      setSelectedFile(acceptedFiles[0]);
    }
  }, []);

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: {
      'application/pdf': ['.pdf'],
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': ['.docx'],
    },
    multiple: false,
  });

  const handleSubmit = async () => {
    if (mode === 'text') {
      if (!content.trim()) {
        toast.error('Please enter the job description content');
        return;
      }

      setSubmitting(true);
      try {
        const response = await axios.post(`${API}/job-descriptions`, {
          title: title.trim() || null,
          content: content.trim(),
        });
        toast.success('Job description created successfully!');
        navigate(`/jobs/${response.data.job_id}`);
      } catch (error) {
        console.error('Error creating job:', error);
        toast.error(error.response?.data?.detail || 'Failed to create job description');
      } finally {
        setSubmitting(false);
      }
    } else {
      if (!selectedFile) {
        toast.error('Please select a file to upload');
        return;
      }

      setSubmitting(true);
      try {
        const formData = new FormData();
        formData.append('file', selectedFile);
        const response = await axios.post(`${API}/job-descriptions/upload`, formData, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        toast.success('Job description uploaded and parsed!');
        navigate(`/jobs/${response.data.job_id}`);
      } catch (error) {
        console.error('Error uploading job:', error);
        toast.error(error.response?.data?.detail || 'Failed to upload file');
      } finally {
        setSubmitting(false);
      }
    }
  };

  return (
    <div className="pt-24 pb-12 px-6 sm:px-8 lg:px-12" data-testid="create-job-page">
      <div className="max-w-4xl mx-auto">
        <button
          onClick={() => navigate('/jobs')}
          className="flex items-center space-x-2 text-slate-600 hover:text-[#002FA7] mb-6 transition-colors"
          data-testid="back-to-jobs-button"
        >
          <ArrowLeft size={20} />
          <span className="text-sm font-medium">Back to Job Descriptions</span>
        </button>

        <div className="mb-8">
          <h1 className="text-4xl sm:text-5xl font-medium tracking-tight text-slate-900" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Add Job Description
          </h1>
          <p className="text-base font-normal text-slate-600 leading-relaxed mt-2">
            Paste text or upload a file — AI will extract structured details
          </p>
        </div>

        <div className="inline-flex bg-slate-100 p-1 rounded-lg mb-6" data-testid="mode-toggle">
          <button
            onClick={() => setMode('text')}
            className={`flex items-center space-x-2 px-5 py-2 rounded-md text-sm font-medium transition-all ${
              mode === 'text' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid="mode-text-button"
          >
            <TextT size={16} weight="regular" />
            <span>Paste Text</span>
          </button>
          <button
            onClick={() => setMode('file')}
            className={`flex items-center space-x-2 px-5 py-2 rounded-md text-sm font-medium transition-all ${
              mode === 'file' ? 'bg-white text-slate-900 shadow-sm' : 'text-slate-600 hover:text-slate-900'
            }`}
            data-testid="mode-file-button"
          >
            <UploadSimple size={16} weight="regular" />
            <span>Upload File</span>
          </button>
        </div>

        {mode === 'text' ? (
          <div className="space-y-4" data-testid="text-mode-panel">
            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Job Title <span className="text-slate-400 font-normal">(optional — AI will extract if not provided)</span>
              </label>
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder="e.g. Senior Full-Stack Engineer"
                className="w-full px-4 py-2.5 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent"
                data-testid="job-title-input"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-2">
                Job Description Content <span className="text-red-500">*</span>
              </label>
              <textarea
                value={content}
                onChange={(e) => setContent(e.target.value)}
                placeholder="Paste the full job description here — role, responsibilities, requirements, qualifications, etc."
                rows={14}
                className="w-full px-4 py-3 bg-white border border-slate-200 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#002FA7] focus:border-transparent leading-relaxed"
                data-testid="job-content-textarea"
              />
              <p className="text-xs text-slate-500 mt-2">
                {content.length} characters • Claude Sonnet 4.5 will extract structured fields
              </p>
            </div>
          </div>
        ) : (
          <div data-testid="file-mode-panel">
            {!selectedFile ? (
              <div
                {...getRootProps()}
                className={`upload-zone rounded-lg p-12 text-center cursor-pointer ${
                  isDragActive ? 'drag-active' : ''
                }`}
                data-testid="jd-upload-dropzone"
              >
                <input {...getInputProps()} />
                <div className="flex flex-col items-center">
                  <UploadSimple size={64} weight="duotone" className="text-slate-400 mb-4" />
                  <p className="text-lg font-medium text-slate-800 mb-2">
                    {isDragActive ? 'Drop file here' : 'Drag & drop a job description file'}
                  </p>
                  <p className="text-sm text-slate-600 mb-4">or click to browse</p>
                  <p className="text-xs font-medium tracking-wide text-slate-500 uppercase">
                    Supports PDF and DOCX
                  </p>
                </div>
              </div>
            ) : (
              <div
                className="flex items-center justify-between p-4 bg-white border border-slate-200 rounded-lg"
                data-testid="selected-file-preview"
              >
                <div className="flex items-center space-x-3">
                  {selectedFile.name.endsWith('.pdf') ? (
                    <FilePdf size={32} weight="duotone" className="text-red-500" />
                  ) : (
                    <FileDoc size={32} weight="duotone" className="text-blue-500" />
                  )}
                  <div>
                    <p className="text-sm font-medium text-slate-900">{selectedFile.name}</p>
                    <p className="text-xs text-slate-500">
                      {(selectedFile.size / 1024).toFixed(2)} KB
                    </p>
                  </div>
                </div>
                <button
                  onClick={() => setSelectedFile(null)}
                  className="text-slate-400 hover:text-red-500 transition-colors"
                  data-testid="remove-file-button"
                >
                  <X size={20} />
                </button>
              </div>
            )}
          </div>
        )}

        <div className="mt-8 flex items-center justify-end space-x-3">
          <button
            onClick={() => navigate('/jobs')}
            disabled={submitting}
            className="px-5 py-2.5 border border-slate-300 text-slate-700 rounded-md text-sm font-medium hover:bg-slate-50 transition-colors disabled:opacity-50"
            data-testid="cancel-job-button"
          >
            Cancel
          </button>
          <button
            onClick={handleSubmit}
            disabled={submitting || (mode === 'text' ? !content.trim() : !selectedFile)}
            className="flex items-center space-x-2 btn-primary px-5 py-2.5 rounded-md text-sm font-medium disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid="submit-job-button"
          >
            <Sparkle size={16} weight="fill" />
            <span>{submitting ? 'Processing with AI...' : 'Create Job Description'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

export default CreateJob;
